import { Resume } from 'packages/types'
import { useEffect } from 'react'
import snakeCase from 'lodash/snakeCase'
import { trackInternalEvent } from '@rio/tracking'
import { useParams } from 'react-router-dom'
import {
  CardOperationFunc,
  MoveCardFunc,
  MoveSubItemFunc,
  UpdateCardFunc,
} from 'builder/components/Card'
import {
  AddCustomSectionPayload,
  MoveSectionPayload,
  ResumeSectionId,
  UpdateSimpleFieldFunc,
  selectors,
  SectionNames,
} from 'builder/modules/resumeEditor'
import { GeneralEditorLevels } from 'builder/modules/generalEditor'
import { DeleteSectionFunc, RenameSectionFunc } from 'builder/components/Section'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { Container } from './styles'
import { ResumeScoreContainer } from './components/ResumeScoreContainer/ResumeScoreContainer'
import { ContentSection } from './components/ContentSection/ContentSection'
import { NavigationSection } from './components/NavigationSection/NavigationSection'
import { WORK_EXPERIENCE_ANALYTICS_SECTION_NAME } from './constants'
import { useStepByStepFlow } from './hooks/useStepByStepFlow'
import { useStepByStepFeature } from './hooks/useStepByStepFeature'

type Props = {
  resume: Resume
  updateCard: UpdateCardFunc
  deleteCard: CardOperationFunc
  addCard: (cardId: string, sectionId: ResumeSectionId, options?: { shouldOpen: boolean }) => void
  addCustomSection: (customSectionId: string, options: AddCustomSectionPayload['options']) => void
  moveCard: MoveCardFunc
  moveSection: (payload: MoveSectionPayload) => void
  levels: GeneralEditorLevels
  updateSimpleField: UpdateSimpleFieldFunc
  renameSection: RenameSectionFunc
  deleteSection: DeleteSectionFunc
  appLocale?: string
  onAddCardSubItem?: CardOperationFunc
  onSubItemMove?: MoveSubItemFunc
  isHeaderFullBorderRadius?: boolean
  className?: string
  scorePanelTopOffsetPx?: number
}

export const StepByStepFlow = (props: Props) => {
  const { isHeaderFullBorderRadius, className, scorePanelTopOffsetPx } = props
  const currentStep = useTypedSelector(selectors.stepByStepFlowCurrentStep)
  const resume = useTypedSelector(selectors.resume)
  const routeParams = useParams()
  const resumeId = (routeParams.id && parseInt(routeParams.id)) || 0
  const {
    handleBack,
    handleNext,
    nextButtonText,
    currentIndex,
    sections,
    isBackButtonPassive,
    scoreImprovementSuggestion,
    handleSuggestionClick,
    resumeScore,
    currentSection,
    handleChangeStep,
    handleAIReview,
    handleSkipAIReview,
  } = useStepByStepFlow()
  const { isRbpStepFlowActive } = useStepByStepFeature()

  useEffect(() => {
    const currentStepEventName =
      currentStep === SectionNames.workExperiences
        ? WORK_EXPERIENCE_ANALYTICS_SECTION_NAME
        : snakeCase(currentStep as string)

    if (isRbpStepFlowActive && resume?.id && resume?.id === resumeId) {
      trackInternalEvent('visit_resume_editor', {
        resume_id: resume?.id,
        step: currentStepEventName,
        editor: 'step_by_step',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRbpStepFlowActive, currentStep])

  return (
    <Container data-testid="step-by-step-flow-container" className={className}>
      <ResumeScoreContainer
        suggestion={scoreImprovementSuggestion}
        handleSuggestionClick={handleSuggestionClick}
        points={resumeScore}
        isHeaderFullBorderRadius={isHeaderFullBorderRadius}
        top={scorePanelTopOffsetPx}
      />
      <ContentSection
        {...props}
        currentSection={currentSection}
        handleChangeStep={handleChangeStep}
      />
      <NavigationSection
        {...{
          handleBack,
          handleNext,
          handleSkipAIReview,
          handleAIReview,
          nextButtonText,
          currentIndex,
          sections,
          isBackButtonPassive,
          currentStep,
        }}
      />
    </Container>
  )
}
